import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { css } from "@emotion/react";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import type { Overwrite } from "@mui/types";
import { theme } from "@polifonia/theme";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { ExternalLink } from "@/components/ExternalLink";
import type { ModalProps } from "@/components/Modal";
import { Modal } from "@/components/Modal";
import { Typography } from "@/components/Typography";

namespace ConnectGoogleAdsManagerAccountModal {
  export interface OwnProps {
    open: boolean;
    onClose: () => void;
    onConnect: (customerId: string) => void;
  }

  export type Props = Overwrite<
    Omit<ModalProps, "children" | "handleClose" | "title">,
    OwnProps
  >;
}

const cssContent = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const cssInstructions = css`
  margin: 0;
  padding-left: 21px;
  color: ${theme.colors.staticFgNote};
  font-size: 0.875rem;
  line-height: 20px;

  li {
    margin-bottom: 0;
  }
`;

interface FormValues {
  customerId: string;
}

// Remove all non-digit characters (including hidden characters from copy-paste)
const sanitizeCustomerId = (value: string): string => {
  return value.replace(/\D/g, "");
};

export const ConnectGoogleAdsManagerAccountModal: React.FC<
  ConnectGoogleAdsManagerAccountModal.Props
> = ({ open, onClose, onConnect }) => {
  const { t } = useTranslation();
  const { control, handleSubmit } = useForm<FormValues>({
    mode: "onChange",
    defaultValues: {
      customerId: "",
    },
  });

  const onSubmit = useHandler<SubmitHandler<FormValues>>((values) => {
    onConnect(sanitizeCustomerId(values.customerId));
    onClose();
  });

  const handleModalClose = useHandler<ModalProps["handleClose"]>(
    (_event, reason) => {
      if (reason === "backdropClick") {
        return;
      }
      onClose();
    },
  );

  return (
    <Modal
      open={open}
      handleClose={handleModalClose}
      title={t(
        "applications.googleAds.connectGoogleAdsManagerAccountModal.title",
      )}
      footer={
        <Stack direction="row" justifyContent="flex-end" gap="12px">
          <Button variant="plain" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button variant="primary" onClick={handleSubmit(onSubmit)}>
            {t("common.connect")}
          </Button>
        </Stack>
      }
    >
      <Stack gap="8px" css={cssContent}>
        <Typography
          variant="body"
          fontWeight={500}
          color={theme.colors.staticFgBody}
        >
          {t(
            "applications.googleAds.connectGoogleAdsManagerAccountModal.customerIdTitle",
          )}
        </Typography>
        <ul css={cssInstructions}>
          <li>
            {t(
              "applications.googleAds.connectGoogleAdsManagerAccountModal.instruction1",
            )}
          </li>
          <li>
            {t(
              "applications.googleAds.connectGoogleAdsManagerAccountModal.instruction2",
            )}
          </li>
        </ul>
        <ExternalLink
          href={t(
            "applications.googleAds.connectGoogleAdsManagerAccountModal.learnMore.link",
          )}
          trailingIcon={true}
        >
          {t("common.learnMore")}
        </ExternalLink>
        <Controller
          name="customerId"
          control={control}
          rules={{
            validate: (value) => {
              const sanitizedValue = sanitizeCustomerId(value);
              if (sanitizedValue.length !== 10) {
                return t(
                  "applications.googleAds.connectGoogleAdsManagerAccountModal.validationError",
                );
              }
              return true;
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              autoComplete="off"
              placeholder={t(
                "applications.googleAds.connectGoogleAdsManagerAccountModal.placeholder",
              )}
              error={!!error}
              helperText={error?.message}
              fullWidth
            />
          )}
        />
      </Stack>
    </Modal>
  );
};
